import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import Collapse from '@mui/material/Collapse';
import { Review } from '@model/iceberg/booking/package';
import { TripAdvisorSubRating } from '@model/iceberg/hotel-content/hotel-content';
import { withTheme } from '@emotion/react';
import React, { useState } from 'react';
import { Icon } from '@components/common/generic-icon';
import { Theme, ThemeProps } from '@theme/base';
import { ProductReview } from './ProductReview';
import { ProductReviewSummary } from './ProductReviewSummary';
import { mq } from '@styles/breakpoints';
import { SectionTitle } from '@components/common/section-title';
import { LazyLoadImage } from '@components/common/lazy-load';
import { ReadMore } from '@styles/common';
import { omitBy, isEmpty } from 'lodash';

/* ***************** *
 *       Types       *
 * ***************** */
export interface ProductReviewsGroupProps {
  theme?: Theme;
  testId?: string;
  reviews: Array<Review>;
  initialNumOfReviews?: number;
  tripAdvisorRating?: string;
  tripAdvisorSubRating?: TripAdvisorSubRating;
  isGhosting?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

const TitleContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flexDirection: 'column',
  [mq.medium]: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row'
  }
});

const TripAdvisorImage: any = styled(LazyLoadImage)(({ theme }: ThemeProps) => ({
  height: '100%',
  maxWidth: '100%',
  marginTop: theme.custom.spacing.medium,
  [mq.medium]: {
    margin: 0
  }
}));

const ReviewsWrapper = styled.div(({ theme }: ThemeProps) => ({
  paddingTop: theme.custom.spacing.large
}));

const MoreReviewsWrapper = styled(Collapse)(({ theme }: ThemeProps) => ({
  ['&:not(.MuiCollapse-hidden)']: {
    marginTop: theme.custom.spacing.xLarge
  }
}));

const ReadMoreReviews = styled(ReadMore)({
  paddingBottom: 0,
  overflowY: 'hidden'
});

/* ********************************** *
 *    ProductReviewsGroupComponent    *
 * ********************************** */

export const ProductReviewsGroupComponent = ({
  testId,
  initialNumOfReviews = 2,
  reviews = [],
  tripAdvisorRating,
  tripAdvisorSubRating,
  isGhosting
}: ProductReviewsGroupProps) => {
  const t: any = useI18NextContext();

  /* *** STATE *** */
  const [showAllReviews, setShowAllReviews] = useState<boolean>(false);
  if (!reviews || !reviews.length) {
    return null;
  }

  return (
    <Container data-testid={testId || TestId.productReviewsGroup.main}>
      <TitleContainer>
        <SectionTitle heading={t('product__reviews-group--title')} />
        <TripAdvisorImage
          src="/assets/images/trip-advisor/trip-advisor.png"
          alt="Trip Advisor"
          height={45}
          width={175}
          objectFit="contain"
        />
      </TitleContainer>
      {tripAdvisorRating && tripAdvisorSubRating && !isEmpty(omitBy(tripAdvisorSubRating, (x) => !x)) && (
        <ReviewsWrapper>
          <ProductReviewSummary tripAdvisorRating={tripAdvisorRating} tripAdvisorSubRating={tripAdvisorSubRating} />
        </ReviewsWrapper>
      )}

      <ReviewsWrapper>
        {reviews.slice(0, initialNumOfReviews).map((review: Review, index: number) => (
          <ProductReview key={review.title + index} review={review} isGhosting={isGhosting} />
        ))}
      </ReviewsWrapper>
      <MoreReviewsWrapper in={showAllReviews}>
        {reviews.slice(initialNumOfReviews).map((review: Review, index: number) => (
          <ProductReview key={review.title + index} review={review} />
        ))}
      </MoreReviewsWrapper>
      {!showAllReviews && !isGhosting && (
        <ReadMoreReviews
          data-testid={TestId.productReviewsGroup.readMoreReviewsTrigger}
          onClick={() => setShowAllReviews(!showAllReviews)}
        >
          <span>{t('product__reviews-group--read-more-reviews')}</span>
          <Icon name={showAllReviews ? 'mhi-chevron-up' : 'mhi-chevron-down'} />
        </ReadMoreReviews>
      )}
    </Container>
  );
};

export const ProductReviewsGroup = withTheme(ProductReviewsGroupComponent);
